import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { getAdminUser } from "../api/adminClient";
import { useNexusOptional } from "../ai/NexusProvider";
import { useTheme } from "../context/ThemeContext";
import {
  getAdminWorkspaceDisplayName,
  getDashboardOrbPresentation,
  getWorkspaceBrandStyle,
} from "../utils/workspacePdfBranding";
import { QUICK_ACTIONS } from "./ActionModePanel";
import { useWorkspaceProductDoc } from "./useWorkspaceProductDoc";
import { filterQuickActionsForWorkspace } from "./workspaceProductSettings";
import CommandCenterPanel from "./CommandCenterPanel";
import LivePulseStrip from "./surfaces/LivePulseStrip";
import { buildPlatformCommandContext } from "./assistantPageContext";
import { ROUTES } from "./routes";

// Dashboard never expands inline chat — conversation lives on ROUTES.nexusChat.

const PRIMARY_ACTION_IDS = ["company", "team", "exec", "lite"];
const SECONDARY_ACTION_IDS = ["invite", "profile", "reminder"];
const DASHBOARD_ACTION_IDS = ["reminder", "company", "team", "exec", "lite", "pe", "invite"];
const MORE_ACTION_IDS = ["profile"];

const ACTION_SHORT_LABELS = {
  company: "Company-wide PDF",
  team: "Full team PDF",
  exec: "Executive summary",
  lite: "Lite assessment",
  invite: "Add participants",
  profile: "PDF templates",
  pe: "Portfolio roll-up",
  reminder: "Opened, not finished",
};

function greetingForHour(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function QuickActionCard({ action, onClick, variant = "primary" }) {
  const Icon = action.icon;
  const shortLabel = ACTION_SHORT_LABELS[action.id] || action.description;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`dashboard-quick-action-card dashboard-quick-action-card--${variant} group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/25`}
    >
      <span className="dashboard-quick-action-icon" aria-hidden>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className="dashboard-quick-action-body">
        <span className="dashboard-quick-action-title">{action.label}</span>
        <span className="dashboard-quick-action-desc">{shortLabel}</span>
      </span>
      <span className="dashboard-quick-action-arrow" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </span>
    </button>
  );
}

export default function AdminDashboardPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const nexusPromptFromNav =
    typeof location.state?.nexusPrompt === "string" ? location.state.nexusPrompt.trim() : "";
  const { sidebarCollapsed = true } = useOutletContext() || {};
  const nexus = useNexusOptional();
  const sidebarOpen = !sidebarCollapsed;
  const dark = theme === "dark";
  const user = getAdminUser();
  const panelRef = useRef(null);
  const [showMoreActions, setShowMoreActions] = useState(false);

  useEffect(() => {
    if (!nexus?.registerCommandFocus) return undefined;
    return nexus.registerCommandFocus(() => {
      panelRef.current?.focusInput?.();
      document.getElementById("dashboard-command-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [nexus]);

  const displayName = user?.name?.trim() || user?.email?.split("@")[0] || "Admin";
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);
  const orbPresentation = useMemo(
    () => getDashboardOrbPresentation(),
    [
      user?.tenant_id,
      user?.product_display_name,
      user?.tenant_name,
      nexus?.workspaceBrandingRevision,
    ]
  );
  const workspaceName = useMemo(
    () => getAdminWorkspaceDisplayName(),
    [
      user?.tenant_id,
      user?.product_display_name,
      user?.tenant_name,
      nexus?.workspaceBrandingRevision,
    ]
  );
  const pageBrandStyle = useMemo(() => getWorkspaceBrandStyle(orbPresentation), [orbPresentation]);

  const platformContext = useMemo(() => buildPlatformCommandContext({ currentRoute: ROUTES.dashboard }), []);
  const workspaceProduct = useWorkspaceProductDoc();

  const quickActions = useMemo(
    () => filterQuickActionsForWorkspace(QUICK_ACTIONS, workspaceProduct),
    [workspaceProduct]
  );

  const actionsById = useMemo(
    () => Object.fromEntries(quickActions.map((action) => [action.id, action])),
    [quickActions]
  );

  const visibleActions = useMemo(
    () => DASHBOARD_ACTION_IDS.map((id) => actionsById[id]).filter(Boolean),
    [actionsById]
  );

  const moreActions = useMemo(
    () => MORE_ACTION_IDS.map((id) => actionsById[id]).filter(Boolean),
    [actionsById]
  );

  const continuePreview = useMemo(() => {
    const msgs = nexus?.threadMessages || [];
    if (!msgs.length) return "";
    const lastUser = [...msgs].reverse().find((m) => m.role === "user");
    return (lastUser?.content || "").trim();
  }, [nexus?.threadMessages]);

  const showContinue = Boolean(
    (nexus?.platformSessionId || nexus?.threadMessages?.length) && continuePreview
  );

  const handleWorkflow = (workflowId) => {
    nexus?.startNewChat?.();
    nexus?.bumpConversations?.();
    nexus?.setChatHandoff?.({ mode: "workflow", guideId: workflowId });
    navigate(ROUTES.nexusChat);
  };

  const actionVariant = (id) => {
    if (PRIMARY_ACTION_IDS.includes(id)) return "primary";
    if (SECONDARY_ACTION_IDS.includes(id)) return "secondary";
    return "default";
  };

  return (
    <div
      className={`dashboard-page dashboard-page--v2 ${dark ? "dashboard-page--dark" : "dashboard-page--light"}${
        sidebarOpen ? " dashboard-page--sidebar-open" : ""
      }${
        orbPresentation.variant === "workspace" ? " dashboard-page--workspace" : ""
      }`}
      data-theme={dark ? "dark" : "light"}
      style={pageBrandStyle}
    >
      <div className="dashboard-page-inner dashboard-page-inner--v2 relative z-[1]">
        <div className="dashboard-stack">
          <div className="dashboard-hero-atmosphere">
            <header className="dashboard-hero-v2">
              <p className="dashboard-workspace-eyebrow">{workspaceName}</p>
              <p className="dashboard-greeting">
                {greeting}, {displayName}
              </p>
              <h1 className="dashboard-headline dashboard-headline--v2">
                How can I assist you today?
              </h1>
            </header>

            <section
              id="dashboard-command-section"
              className="dashboard-command-section dashboard-command-section--primary"
            >
              <CommandCenterPanel
                ref={panelRef}
                context={platformContext}
                pathname={ROUTES.dashboard}
                showInsights
                hideWorkflows
                usePlatformChat
                registerWorkflow
                variant="landing"
                commandBarRefined
                initialPrompt={nexusPromptFromNav}
                placeholderOverride="Ask NEXUS anything or type / for reports and actions"
              />
            </section>
          </div>

          {showContinue ? (
            <section className="dashboard-continue-section" aria-label="Continue conversation">
              <button
                type="button"
                className="dashboard-continue-card"
                onClick={() => navigate(ROUTES.nexusChat)}
              >
                <span className="dashboard-continue-label">Continue where you left off</span>
                <span className="dashboard-continue-preview">{continuePreview}</span>
                <span className="dashboard-continue-arrow" aria-hidden>→</span>
              </button>
            </section>
          ) : null}

          <section className="dashboard-quick-actions" aria-labelledby="dashboard-quick-actions-title">
            <h2 id="dashboard-quick-actions-title" className="dashboard-section-heading">
              Quick actions
            </h2>
            <div className="dashboard-quick-actions-grid">
              {visibleActions.map((action) => (
                <QuickActionCard
                  key={action.id}
                  action={action}
                  variant={actionVariant(action.id)}
                  onClick={() => handleWorkflow(action.id)}
                />
              ))}
            </div>
            {moreActions.length ? (
              <div className="dashboard-quick-actions-more">
                <div
                  className={`dashboard-quick-actions-expandable${
                    showMoreActions ? " dashboard-quick-actions-expandable--open" : ""
                  }`}
                  aria-hidden={!showMoreActions}
                >
                  <div className="dashboard-quick-actions-expandable-inner">
                    <div className="dashboard-quick-actions-grid dashboard-quick-actions-grid--more">
                      {moreActions.map((action) => (
                        <QuickActionCard
                          key={action.id}
                          action={action}
                          variant={actionVariant(action.id)}
                          onClick={() => handleWorkflow(action.id)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="dashboard-quick-actions-more-toggle"
                  aria-expanded={showMoreActions}
                  onClick={() => setShowMoreActions((open) => !open)}
                >
                  <span>{showMoreActions ? "Show fewer actions" : "View all actions"}</span>
                  <span className="dashboard-quick-actions-more-chevron" aria-hidden>
                    {showMoreActions ? "↑" : "↓"}
                  </span>
                </button>
              </div>
            ) : null}
          </section>

          <section className="dashboard-live-pulse-section" aria-label="Live workspace pulse">
            <LivePulseStrip dark={dark} layout="dashboard" />
          </section>
        </div>
      </div>
    </div>
  );
}
